import { t } from '@lingui/macro'
import { useFormikContext } from 'formik'
import { useId } from 'react'
import styled, { useTheme } from 'styled-components'

import { Checkbox } from '@/@bothrs-ui/web/components/forms/checkbox/Checkbox'
import { FlexColumn } from '@/@bothrs-ui/web/components/layout'
import { Span } from '@/@bothrs-ui/web/components/typography/Body'

import type { AllergyCodeReaction } from '@/modules/app/types/HealthParameter.types'
import type { AllergyFormValues } from './AllergyItemForm'

// ================================ Constants ================================

const MAX_REACTIONS = 2

export const REACTION_INPUT_MIN_HEIGHT = 340

// ================================ Types ================================

type AllergyReactionStructuredInputProps = {
  availableReactions: AllergyCodeReaction[]
}

// ================================ Component ================================

export const AllergyReactionStructuredInput = ({ availableReactions }: AllergyReactionStructuredInputProps) => {
  const reactionsLabelId = useId()
  const theme = useTheme()
  const { values, setFieldValue } = useFormikContext<AllergyFormValues>()

  const toggleReaction = (reaction: AllergyCodeReaction) => {
    const isSelected = values.selectedReactions.some((r) => r.id === reaction.id)
    if (!isSelected && values.selectedReactions.length >= MAX_REACTIONS) return
    const next = isSelected
      ? values.selectedReactions.filter((r) => r.id !== reaction.id)
      : [...values.selectedReactions, reaction]
    setFieldValue('selectedReactions', next)
  }

  return (
    <FlexColumn gap={theme.UI.Spacing.Space2} style={{ minHeight: REACTION_INPUT_MIN_HEIGHT }}>
      <Span
        id={reactionsLabelId}
        type='BodyLabel'
        color={theme.Colors.Neutrals.Darkest}
        style={{ textTransform: 'uppercase' }}
      >
        {t({
          id: 'allergy.form.reactions.label',
          message: 'Reactie(s)',
        })}
      </Span>
      <FlexColumn gap={theme.UI.Spacing.Space2}>
        {availableReactions.map((reaction) => {
          const isSelected = values.selectedReactions.some((r) => r.id === reaction.id)
          const isDisabled = !isSelected && values.selectedReactions.length >= MAX_REACTIONS
          return (
            <StCheckboxRow
              key={reaction.id}
              $selected={isSelected}
              $disabled={isDisabled}
              onClick={(event) => {
                if ((event.target as HTMLElement).closest('input, label')) return
                toggleReaction(reaction)
              }}
            >
              <Checkbox
                id={`reaction-${reaction.id}`}
                name='selectedReactions'
                type='checkbox'
                label={reaction.description ?? ''}
                checked={isSelected}
                onChange={() => toggleReaction(reaction)}
              />
              {reaction.displayName && (
                <span
                  style={{
                    display: 'block',
                    fontSize: '16px',
                    color: theme.Components.Conversational.Text,
                    fontWeight: 400,
                    fontStyle: 'italic',
                    paddingTop: theme.UI.SpacingPx.Space2,
                    paddingLeft: theme.UI.SpacingPx.Space6,
                  }}
                >
                  {reaction.displayName}
                </span>
              )}
            </StCheckboxRow>
          )
        })}
      </FlexColumn>
    </FlexColumn>
  )
}

// ================================ Styled Components ================================

const StCheckboxRow = styled.div<{
  $selected: boolean
  $disabled: boolean
}>`
  padding: ${({ theme }) => `${theme.UI.SpacingPx.Space3} ${theme.UI.SpacingPx.Space4}`};
  border: 1px solid
    ${({ theme, $selected }) =>
      $selected ? theme.Components.Conversational.ActiveBorder : theme.Components.Conversational.Border};
  border-radius: ${({ theme }) => theme.UI.BorderRadius.Small}px;
  background-color: ${({ theme, $selected }) =>
    $selected ? theme.Components.Conversational.ActiveFill : theme.Components.Conversational.Fill};
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ $disabled }) => ($disabled ? 0.4 : 1)};

  > div {
    align-items: center;

    > div:first-child {
      margin-top: 0;
    }

    > label {
      line-height: 1;
    }
  }
`
